import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowLeftRight,
  ArrowUpRight,
  Check,
  CheckCircle2,
  Clock3,
  Scale,
  Target,
} from 'lucide-react';
import { apiFetch, isNotFound } from '../../lib/api';
import { siteLabel } from '../../lib/sites';
import { formatSourceDate } from '../../lib/formatting';
import { calendarEventUrl, followUpMailto } from '../../lib/action-links';
import { verbLine, snoozeLine, awaitingLabel, askQuestionForConflict } from '../../lib/conflict-copy';
import {
  planGroupResolve,
  isEvalOnlyBand,
  type ConflictCardResponse,
  type ResolveBody,
} from '../brief/ConflictResolveModal';
import { STREAM_LABELS, STREAM_ACTION } from '../brief/primitives';
import { useTeamSettings, useCurrentUserId } from '../../hooks/useTeamSettings';
import { useTeamResolutions, useDisputeResolution, type ResolutionRecord } from '../../hooks/useTeamResolutions';
import type { TeamTodaySurfaceData, BriefTask } from '../../hooks/useTeamTodaySurface';
import {
  useConflictOutcomes,
  recordConflictOutcome,
  outcomeClears,
  isSessionRecordedVerb,
  type ConflictOutcome,
} from '../../lib/conflict-local';
import {
  IndexGroup,
  MastTile,
  PageMast,
  Slide,
  SlideSectionLabel,
  WorkroomGrid,
  useScrollSpy,
  useSlideScroll,
  type ItemRefMap,
} from '../shared/workroom';

/**
 * Actions — the workroom (redesign port; the one surface that is a BUILD, not a re-skin).
 * The side pane is a boxed INDEX into the main column, which stacks every item's workspace in
 * section order (Conflicts → Tasks → Paused → The record); slide-and-lock scroll-snap + the
 * scroll-spy reading line live in shared/workroom.tsx.
 *
 * Wiring honesty (the handoff law — wire what exists, placeholder the rest):
 * - Conflicts: REAL. Items are the brief's conflict tasks; each room fetches the composed
 *   two-decision card (POST /conflicts/[edgeKey]/card — same cache key as the brief's popup)
 *   and resolves through the real verb rail (decision / custom / not_conflict / snooze
 *   timer|until_inflow / blocked), with the popup's group fan-out (planGroupResolve).
 * - Tasks: the brief's mechanical to-dos (linear / github_review / meeting_prep / …) — real
 *   items with real click-outs; "Mark done" is a client-only acknowledgement (no done rail,
 *   same as the pre-redesign brief tick).
 * - Paused: conflicts snoozed THIS SESSION (the durable per-user snooze rail has no read
 *   endpoint yet, so earlier snoozes simply don't reappear in the brief payload).
 * - The record: REAL (mock-alignment 2026-07-26) — the durable history via GET
 *   /api/surfaces/team/resolutions (decision_resolutions). Once loaded it replaces the
 *   session-local rows (the resolve route writes the registry before responding). A
 *   record-side Dispute verb still waits on a dispute-by-pair route: the resolve route is
 *   keyed on a briefing item, which a record row doesn't carry — objection stays on the
 *   conflict card when the pair resurfaces.
 */

const verbClass =
  'rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3.5 py-1.5 text-sm transition hover:border-foreground hover:bg-foreground hover:text-card disabled:cursor-default disabled:opacity-50 dark:border-border dark:bg-card';
const primaryVerbClass =
  'rounded-full bg-foreground px-3.5 py-1.5 text-sm text-card transition hover:opacity-90 disabled:cursor-default disabled:opacity-50';

interface ActionsSurfaceProps {
  data: TeamTodaySurfaceData;
  onNav: (id: string) => void;
}

// Tomorrow 09:00 local, as ISO — the snooze timer's wake time.
function tomorrowNineAmIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

const shortDate = formatSourceDate;

/**
 * The record's HEADLINE — the subject of the settlement, not the settlement sentence.
 *
 * `settledText` is prose written by whoever settled it (or a verbatim decision snapshot), so using
 * it as the title gave every record row a three-or-four-line serif headline that pushed the who /
 * when / positions out of view. Prefer the topic, which is already a short subject; fall back to a
 * clause of the sentence rather than the whole thing. The full sentence still renders below, in the
 * "What" line, so nothing is hidden — it just stops being the headline.
 */
export function recordTitle(record: { topic?: string | null; settledText?: string | null }): string {
  const topic = record.topic?.trim();
  if (topic) return topic;

  const text = record.settledText?.trim();
  if (!text) return 'Settled decision';

  // First clause — up to the first sentence break — then a hard character cap with a word boundary.
  const firstClause = text.split(/(?<=[.!?])\s|\s[—–]\s/)[0].trim().replace(/[.,;:]$/, ''); // dash-ok: functional regex over legacy data
  if (firstClause.length <= 60) return firstClause;
  const cut = firstClause.slice(0, 60);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 30 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

type SectionLabel = 'Conflicts' | 'Tasks' | 'Paused' | 'The record';

export function ActionsSurface({ data, onNav }: ActionsSurfaceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLDivElement | null>()) as ItemRefMap;
  const outcomes = useConflictOutcomes();
  const [doneTasks, setDoneTasks] = useState<Set<string>>(() => new Set());

  const tasks = data.data?.tasks ?? [];
  const conflictTasks = tasks.filter((t): t is BriefTask & { briefingItemId: string } => t.stream === 'conflict' && !!t.briefingItemId);
  const openConflicts = conflictTasks.filter((t) => !outcomeClears(outcomes.get(t.briefingItemId)));
  const pausedConflicts = conflictTasks.filter((t) => outcomes.get(t.briefingItemId)?.verb === 'snooze');
  const mechTasks = tasks.filter((t) => t.stream !== 'conflict' && t.stream !== 'focus');
  const openMechCount = mechTasks.filter((t) => !doneTasks.has(t.taskId)).length;
  // Session record: terminal outcomes (settled / not-a-conflict), newest last.
  const sessionRecord = [...outcomes.entries()].filter(
    ([, o]) => o.verb === 'decision' || o.verb === 'custom' || o.verb === 'not_conflict',
  );

  // The DURABLE record. Once loaded it is the superset of this session's settles (the resolve
  // route writes decision_resolutions before responding, and the modal invalidates this key),
  // so a loaded record REPLACES the session-local rows. Until then (loading / error / no team)
  // the session view + an honest rail slide keep rendering.
  const recordQuery = useTeamResolutions();
  // Any SETTLED response counts as loaded — including {status:'no_team'} (review finding: gating on
  // status==='ready' alone left a team-less viewer on "loading…" forever). no_team renders the same
  // honest empty record as a team with no settlements.
  const durableReady = recordQuery.data != null;
  const durableRecord: ResolutionRecord[] =
    recordQuery.data?.status === 'ready' ? (recordQuery.data.resolutions ?? []) : [];
  const recordIds = durableReady
    ? durableRecord.length > 0
      ? durableRecord.map((r) => `record-${r.id}`)
      : ['record-rail']
    : [...sessionRecord.map(([k]) => `record-${k}`), 'record-rail'];
  const settledCount = durableReady ? durableRecord.length : sessionRecord.length;

  const sections: Array<{ label: SectionLabel; ids: string[] }> = [
    { label: 'Conflicts' as const, ids: openConflicts.map((t) => t.briefingItemId) },
    { label: 'Tasks' as const, ids: mechTasks.map((t) => t.taskId) },
    { label: 'Paused' as const, ids: pausedConflicts.map((t) => `paused-${t.briefingItemId}`) },
    { label: 'The record' as const, ids: recordIds },
  ].filter((s) => s.ids.length > 0);

  const firstId = sections[0]?.ids[0] ?? 'record-rail';
  const { visibleId, jump } = useScrollSpy(itemRefs, firstId, [tasks, outcomes, doneTasks, durableRecord.length], containerRef);
  useSlideScroll(containerRef);
  const activeSection = sections.find((s) => s.ids.includes(visibleId))?.label;

  const needsYou = openConflicts.length + openMechCount;
  const restCount = pausedConflicts.length + settledCount;

  const markDone = (taskId: string) =>
    setDoneTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });

  return (
    <div
      ref={containerRef}
      className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground selection:bg-[#d9b298] selection:text-[#3e2f2a]"
    >
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        <PageMast
          eyebrow="Actions · the working set"
          title={
            <>
              Waiting on <em>you.</em>
            </>
          }
          intro="Every open conflict and task, the paused set, and the record of what settled. The brief shows today's cut; this page holds the whole."
          tiles={
            <>
              <MastTile
                label="NEED YOU NOW"
                value={needsYou}
                note={`${openConflicts.length} conflict${openConflicts.length === 1 ? '' : 's'} · ${openMechCount} task${openMechCount === 1 ? '' : 's'}`}
              />
              <MastTile
                label="THE REST"
                value={restCount}
                note={`${pausedConflicts.length} paused · ${settledCount} settled${durableReady ? '' : ' this session'}`}
              />
            </>
          }
        />

        <WorkroomGrid
          index={
            <>
              {needsYou === 0 && (
                <div className="mb-4 flex items-start gap-3 rounded-2xl border border-border bg-[#fbf7ef] p-4 dark:bg-muted">
                  <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-[#e4eadf] text-[#62725c]">
                    <Activity size={17} />
                  </span>
                  <div>
                    <p className="text-[15px] font-semibold leading-5">All clear</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Nothing needs you. {pausedConflicts.length > 0 ? `${pausedConflicts.length} paused · ` : ''}the record is current.
                    </p>
                  </div>
                </div>
              )}
              {openConflicts.length > 0 && (
                <IndexGroup label={`Conflicts · ${openConflicts.length}`} active={activeSection === 'Conflicts'}>
                  {openConflicts.map((t) => (
                    <IndexRow
                      key={t.briefingItemId}
                      icon="conflict"
                      title={t.title}
                      marks={indexMarks(t, outcomes.get(t.briefingItemId))}
                      active={visibleId === t.briefingItemId}
                      onPick={() => jump(t.briefingItemId)}
                    />
                  ))}
                </IndexGroup>
              )}
              {mechTasks.length > 0 && (
                <IndexGroup label={`Tasks · ${openMechCount}`} active={activeSection === 'Tasks'}>
                  {mechTasks.map((t) => (
                    <IndexRow
                      key={t.taskId}
                      icon={doneTasks.has(t.taskId) ? 'done' : 'task'}
                      title={t.title}
                      marks={doneTasks.has(t.taskId) ? [{ text: 'done', tone: 'mute' }] : t.dueLabel ? [{ text: t.dueLabel, tone: 'sand' }] : []}
                      done={doneTasks.has(t.taskId)}
                      active={visibleId === t.taskId}
                      onPick={() => jump(t.taskId)}
                    />
                  ))}
                </IndexGroup>
              )}
              {pausedConflicts.length > 0 && (
                <IndexGroup label={`Paused · ${pausedConflicts.length}`} active={activeSection === 'Paused'}>
                  {pausedConflicts.map((t) => (
                    <IndexRow
                      key={t.briefingItemId}
                      icon="paused"
                      title={t.title}
                      marks={[{ text: outcomes.get(t.briefingItemId)?.wake ?? 'snoozed', tone: 'sand' }]}
                      active={visibleId === `paused-${t.briefingItemId}`}
                      onPick={() => jump(`paused-${t.briefingItemId}`)}
                    />
                  ))}
                </IndexGroup>
              )}
              <div className="mt-5">
                <p
                  className={`px-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] transition-colors ${activeSection === 'The record' ? 'text-acc' : 'text-muted-foreground'}`}
                >
                  The record{settledCount > 0 ? ` · ${settledCount}` : ''}
                </p>
                <div className="mt-2 rounded-2xl border border-[#2a2447] bg-[#322b4d] p-2 text-[#fbf5e9] shadow-[0_12px_26px_rgba(74,58,107,.14)]">
                  {durableReady ? (
                    durableRecord.length > 0 ? (
                      durableRecord.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => jump(`record-${r.id}`)}
                          className={`w-full rounded-xl px-2.5 py-2 text-left transition ${visibleId === `record-${r.id}` ? 'bg-white/[.12]' : 'hover:bg-white/[.06]'}`}
                        >
                          <span className="flex items-baseline justify-between gap-2">
                            <span className={`text-[13px] font-medium leading-4 ${r.status !== 'resolved' ? 'text-[#c2bcd2]' : ''}`}>
                              {recordTitle(r)}
                            </span>
                            <span className="whitespace-nowrap font-['DM_Mono'] text-[9px] text-[#c7c2d4]">{shortDate(r.resolvedAt)}</span>
                          </span>
                          <span className="mt-0.5 block text-[11px] leading-4 text-[#c2bcd2]">
                            {r.resolvedByName ?? 'a teammate'} ·{' '}
                            {r.status === 'dismissed' ? 'dismissed · not a conflict' : r.status === 'disputed' ? 'disputed' : 'settled'}
                          </span>
                        </button>
                      ))
                    ) : (
                      <button
                        onClick={() => jump('record-rail')}
                        className={`w-full rounded-xl px-2.5 py-2 text-left transition ${visibleId === 'record-rail' ? 'bg-white/[.12]' : 'hover:bg-white/[.06]'}`}
                      >
                        <span className="block text-[13px] font-medium leading-4 text-[#c2bcd2]">Nothing settled yet</span>
                        <span className="mt-0.5 block text-[11px] leading-4 text-[#8d86a8]">resolutions land here</span>
                      </button>
                    )
                  ) : (
                    <>
                      {sessionRecord.map(([edgeKey, o]) => (
                        <button
                          key={edgeKey}
                          onClick={() => jump(`record-${edgeKey}`)}
                          className={`w-full rounded-xl px-2.5 py-2 text-left transition ${visibleId === `record-${edgeKey}` ? 'bg-white/[.12]' : 'hover:bg-white/[.06]'}`}
                        >
                          <span className="flex items-baseline justify-between gap-2">
                            <span className={`text-[13px] font-medium leading-4 ${o.verb === 'not_conflict' ? 'text-[#c2bcd2]' : ''}`}>
                              {o.title ?? 'Settled conflict'}
                            </span>
                            <span className="whitespace-nowrap font-['DM_Mono'] text-[9px] text-[#c7c2d4]">just now</span>
                          </span>
                          <span className="mt-0.5 block text-[11px] leading-4 text-[#c2bcd2]">
                            you · {o.verb === 'not_conflict' ? 'dismissed · not a conflict' : 'settled · propagating'}
                          </span>
                        </button>
                      ))}
                      <button
                        onClick={() => jump('record-rail')}
                        className={`w-full rounded-xl px-2.5 py-2 text-left transition ${visibleId === 'record-rail' ? 'bg-white/[.12]' : 'hover:bg-white/[.06]'}`}
                      >
                        <span className="block text-[13px] font-medium leading-4 text-[#c2bcd2]">Past resolutions</span>
                        <span className="mt-0.5 block text-[11px] leading-4 text-[#8d86a8]">
                          {recordQuery.isError ? "couldn't load" : 'loading…'}
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          }
        >
          {openConflicts.length > 0 && (
            <div>
              <SlideSectionLabel>Conflicts</SlideSectionLabel>
              <div className="space-y-4">
                {openConflicts.map((t) => (
                  <Slide key={t.briefingItemId} id={t.briefingItemId} itemRefs={itemRefs}>
                    <ConflictRoom task={t} onNav={onNav} outcome={outcomes.get(t.briefingItemId)} />
                  </Slide>
                ))}
              </div>
            </div>
          )}

          {mechTasks.length > 0 && (
            <div className={openConflicts.length > 0 ? 'mt-8' : ''}>
              <SlideSectionLabel>Tasks</SlideSectionLabel>
              <div className="space-y-4">
                {mechTasks.map((t) => (
                  <Slide key={t.taskId} id={t.taskId} itemRefs={itemRefs}>
                    <TaskRoom task={t} onNav={onNav} done={doneTasks.has(t.taskId)} onToggleDone={() => markDone(t.taskId)} />
                  </Slide>
                ))}
              </div>
            </div>
          )}

          {pausedConflicts.length > 0 && (
            <div className="mt-8">
              <SlideSectionLabel>Paused</SlideSectionLabel>
              <div className="space-y-4">
                {pausedConflicts.map((t) => (
                  <Slide key={`paused-${t.briefingItemId}`} id={`paused-${t.briefingItemId}`} itemRefs={itemRefs}>
                    <PausedRoom task={t} wake={outcomes.get(t.briefingItemId)?.wake} />
                  </Slide>
                ))}
              </div>
            </div>
          )}

          <div className={sections.length > 1 || openConflicts.length + mechTasks.length + pausedConflicts.length > 0 ? 'mt-8' : ''}>
            <SlideSectionLabel>The record</SlideSectionLabel>
            <div className="space-y-4">
              {durableReady ? (
                durableRecord.length > 0 ? (
                  durableRecord.map((r) => (
                    <Slide key={`record-${r.id}`} id={`record-${r.id}`} itemRefs={itemRefs}>
                      <RecordRoom record={r} onNav={onNav} />
                    </Slide>
                  ))
                ) : (
                  <Slide id="record-rail" itemRefs={itemRefs}>
                    <section className="rounded-[24px] border border-dashed border-[#d3c6b2] bg-card/60 p-5 sm:p-6 dark:border-border">
                      <div className="flex items-start gap-3">
                        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-muted text-muted-foreground">
                          <Scale size={19} />
                        </span>
                        <div>
                          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">The record</p>
                          <h2 className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em] text-muted-foreground">Nothing settled yet.</h2>
                        </div>
                      </div>
                      <p className="mt-3 max-w-[560px] text-[15px] leading-6 text-muted-foreground">
                        When a conflict is resolved - a side chosen, an answer typed, or a pair marked not-a-conflict - 
                        the settlement is recorded durably and renders here with who · how · when.
                      </p>
                    </section>
                  </Slide>
                )
              ) : (
                <>
                  {sessionRecord.map(([edgeKey, o]) => (
                    <Slide key={`record-${edgeKey}`} id={`record-${edgeKey}`} itemRefs={itemRefs}>
                      <SessionRecordRoom outcome={o} />
                    </Slide>
                  ))}
                  <Slide id="record-rail" itemRefs={itemRefs}>
                    <section className="rounded-[24px] border border-dashed border-[#d3c6b2] bg-card/60 p-5 sm:p-6 dark:border-border">
                      <div className="flex items-start gap-3">
                        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-muted text-muted-foreground">
                          <Scale size={19} />
                        </span>
                        <div>
                          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">The record</p>
                          <h2 className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em] text-muted-foreground">
                            {recordQuery.isError ? 'The record couldn’t load right now.' : 'Loading the record…'}
                          </h2>
                        </div>
                      </div>
                      {recordQuery.isError && (
                        <p className="mt-3 max-w-[560px] text-[15px] leading-6 text-muted-foreground">
                          Nothing here is missing on purpose - the durable history exists and will render when the
                          read succeeds.
                        </p>
                      )}
                    </section>
                  </Slide>
                </>
              )}
            </div>
          </div>
        </WorkroomGrid>
      </div>
    </div>
  );
}

// ---- index rows ----

type MarkTone = 'coral' | 'blue' | 'sand' | 'mute';
const MARK_TONE: Record<MarkTone, string> = {
  coral: 'text-[#b95c4b]',
  blue: 'text-[#527c90]',
  sand: 'text-[#887157]',
  mute: 'text-muted-foreground',
};

function indexMarks(task: BriefTask, outcome: ConflictOutcome | undefined): Array<{ text: string; tone: MarkTone }> {
  const marks: Array<{ text: string; tone: MarkTone }> = [];
  if (outcome?.verb === 'blocked') marks.push({ text: 'blocked - routed', tone: 'coral' });
  if (isEvalOnlyBand(task.band)) marks.push({ text: 'possible conflict', tone: 'sand' });
  // A demoted conflict (escalated:false) is NOT on the brief — it lives here. Claiming otherwise would
  // send the reader back to a brief that doesn't show it. Nullish = legacy payload, still on the brief.
  marks.push(
    task.escalated === false
      ? { text: 'not on today’s brief', tone: 'mute' }
      : { text: "on today's brief", tone: 'blue' },
  );
  return marks;
}

function IndexRow({
  icon,
  title,
  marks,
  active,
  done,
  onPick,
}: {
  icon: 'conflict' | 'task' | 'paused' | 'done';
  title: string;
  marks: Array<{ text: string; tone: MarkTone }>;
  active: boolean;
  done?: boolean;
  onPick: () => void;
}) {
  const Icon = icon === 'done' ? Check : icon === 'conflict' ? ArrowLeftRight : icon === 'paused' ? Clock3 : Target;
  const iconClass =
    icon === 'done'
      ? 'bg-[#e4eadf] text-[#62725c]'
      : icon === 'conflict'
        ? 'bg-[#f5d8ce] text-[#b95c4b]'
        : icon === 'paused'
          ? 'bg-[#eee5d5] text-[#887157]'
          : 'bg-[#dce8ec] text-[#527c90]';
  return (
    <button
      onClick={onPick}
      className={`flex w-full items-start gap-2.5 rounded-xl border p-2.5 text-left transition ${active ? 'border-acc bg-acc-soft' : 'border-transparent hover:bg-[#fbf7ef] dark:hover:bg-muted'}`}
    >
      <span className={`mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg ${iconClass}`}>
        <Icon size={14} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-medium leading-5 ${done ? 'text-muted-foreground line-through' : ''}`}>{title}</span>
        {marks.length > 0 && (
          <span className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
            {marks.map((mark) => (
              <span key={mark.text} className={`font-['DM_Mono'] text-[9px] uppercase tracking-[.08em] ${MARK_TONE[mark.tone]}`}>
                {mark.text}
              </span>
            ))}
          </span>
        )}
      </span>
    </button>
  );
}

// ---- rooms ----

/** Open-conflict workspace: the composed two-decision card + the real verb rail. */
function ConflictRoom({
  task,
  onNav,
  outcome,
}: {
  task: BriefTask & { briefingItemId: string };
  onNav: (id: string) => void;
  outcome: ConflictOutcome | undefined;
}) {
  const edgeKey = task.briefingItemId;
  const queryClient = useQueryClient();
  const team = useTeamSettings();
  const currentUserId = useCurrentUserId();
  const [mode, setMode] = useState<'eval' | 'full'>(isEvalOnlyBand(task.band) ? 'eval' : 'full');
  const [customOpen, setCustomOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [teammateId, setTeammateId] = useState('');
  const [pingNote, setPingNote] = useState('');
  const [note, setNote] = useState<string | null>(null);

  // Same cache entry as the brief's popup — opening the popup then this page composes once.
  const card = useQuery({
    queryKey: ['conflict-card', edgeKey],
    queryFn: () => apiFetch<ConflictCardResponse>(`/api/surfaces/team/conflicts/${edgeKey}/card`, { method: 'POST' }),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });
  const options = card.data?.options ?? [];
  const composed = card.data?.composed ?? false;
  // The two decisions could not be shown. The conflict itself is still actionable — resolve keys on
  // task.briefingItemId, not on the card — so this gates the POSITIONS, never the verb rail.
  const cardUnavailable = card.isError || options.length === 0;
  const title = (composed && card.data?.headline) || card.data?.topic || task.title;
  // The durable, team-wide receipt for an outstanding ping/blocked/route. It WINS over the session-local
  // outcome below: the server's row survives a reload and knows who is being waited on, which the
  // session store never did — that gap is why these verbs read as no-ops (bug-557).
  const awaiting = card.data?.awaiting ?? null;

  const postResolve = (itemId: string, body: ResolveBody) =>
    apiFetch<{ ok: boolean }>(`/api/surfaces/team/conflicts/${itemId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

  const resolve = useMutation({
    mutationFn: async (body: ResolveBody) => {
      const plan = planGroupResolve(edgeKey, body, task.memberBriefingItemIds, options);
      await postResolve(plan.primary.itemId, plan.primary.body);
      if (plan.siblings.length > 0) {
        await Promise.all(
          plan.siblings.map((s) =>
            postResolve(s.itemId, s.body).catch((err) =>
              console.warn('[actions/resolve] group fan-out failed for member', s.itemId, err),
            ),
          ),
        );
      }
    },
    onSuccess: (_data, body) => {
      const how =
        body.choice === 'decision'
          ? options.find((o) => o.decisionId === body.decisionId)?.decisionText
          : body.choice === 'custom'
            ? body.resolutionText
            : undefined;
      // ping and route are NOT session outcomes: the local store models only the five verbs that change
      // whether the conflict still needs you, and `outcomeClears` treats anything but `blocked` as
      // cleared — so recording a route here would silently drop a still-open conflict off the lane.
      // Their receipt is the server's awaiting row, refetched below. Narrow, never cast (the cast is
      // what let this diverge between the two conflict surfaces in the first place).
      if (isSessionRecordedVerb(body.choice)) {
        recordConflictOutcome(edgeKey, {
          verb: body.choice,
          how,
          wake: body.choice === 'snooze' ? (body.snoozeMode === 'timer' ? 'wakes tomorrow 9a' : "until it's in flow") : undefined,
          title,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-today'] });
      // The awaiting annotation is written by this same request, so refetch the card to show the receipt.
      if (body.choice === 'ping' || body.choice === 'route' || body.choice === 'blocked') {
        queryClient.invalidateQueries({ queryKey: ['conflict-card', edgeKey] });
      }
      const target = teammates.find((m) => m.user_id === (body.routeUserId ?? body.blockedOnUserId ?? body.pingUserId))?.display_name;
      setNote(
        body.choice === 'decision' || body.choice === 'custom'
          ? `Settled - ${how}. The record is updated and propagation starts with everyone it touches.`
          : body.choice === 'not_conflict'
            ? 'Marked not a conflict. Recorded, in case it resurfaces.'
            : body.choice === 'snooze'
              ? 'Snoozed.'
              : body.choice === 'route'
                ? `Routed to ${target ?? 'them'}. No message was sent - it appears in their next brief.`
                : body.choice === 'ping'
                  ? `Pinged ${target ?? 'them'} with your note.`
                  : `Told ${target ?? 'them'} you're blocked on this. It stays open with the blocker named.`,
      );
    },
    onError: (err) => console.warn('[actions/resolve] failed; nothing was changed', err),
  });

  const busy = resolve.isPending;
  const me = currentUserId.data;
  const teammates = me ? (team.data?.members ?? []).filter((m) => m.user_id !== me) : [];
  const blocked = outcome?.verb === 'blocked';

  return (
    <section className="rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-acc-soft text-acc">
          <Scale size={19} />
        </span>
        <div>
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">
            {mode === 'eval' ? 'Possible conflict · confirm it' : 'Conflict'}
          </p>
          <h2 className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em]">{title}</h2>
        </div>
      </div>
      {(composed && card.data?.standfirst) || task.detail ? (
        <p className="mt-3 max-w-[560px] text-[15px] leading-6 text-muted-foreground">
          {(composed && card.data?.standfirst) || task.detail}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {awaiting ? (
          <span className="rounded-full bg-[#f5d8ce] px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.06em] text-[#b95c4b]">
            {awaitingLabel(awaiting.kind, awaiting.targetName ?? null)}
          </span>
        ) : blocked ? (
          <span className="rounded-full bg-[#f5d8ce] px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.06em] text-[#b95c4b]">
            blocked - waiting on an answer
          </span>
        ) : null}
        {/* Demoted conflicts (escalated:false) are deliberately NOT on the brief — this room is where
            they live, so the chip has to say which it is rather than assert a brief slot it lost. */}
        {task.escalated === false ? (
          <span className="rounded-full bg-muted px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.06em] text-muted-foreground">
            not on today&rsquo;s brief
          </span>
        ) : (
          <span className="rounded-full bg-[#dce8ec] px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.06em] text-[#527c90]">
            on today's brief
          </span>
        )}
        {task.dueLabel && (
          <span className="rounded-full bg-[#eee5d5] px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.06em] text-[#887157]">
            {task.dueLabel}
          </span>
        )}
      </div>

      {card.isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading the two decisions…</p>
      ) : cardUnavailable ? (
        <div className="mt-4">
          <p className="text-sm text-destructive">
            {isNotFound(card.error)
              ? "This conflict's underlying decisions have changed - it can't be opened anymore."
              : "Couldn't load this conflict's decisions right now. Nothing was changed."}
          </p>
          {/* The card query is retry:false, so a transient failure used to be terminal. */}
          {!isNotFound(card.error) && (
            <button onClick={() => void card.refetch()} className={`${verbClass} mt-3`}>
              Try again
            </button>
          )}
        </div>
      ) : (
        <>
          {/* ONE card per side. The position and the path you'd pick are the same decision, so they are
              the same card — rendering the decision text twice (quote above, bold below) said nothing
              new the second time and read as duplication when the LLM gloss was absent. */}
          <div className="mt-4">
            <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">
              {mode === 'full' ? 'Choose a resolution - choosing settles it' : 'The two positions'}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {options.map((opt, index) => (
                <PositionCard
                  key={opt.decisionId}
                  option={opt}
                  onNav={onNav}
                  accented={index === 0}
                  onChoose={mode === 'full' ? () => resolve.mutate({ choice: 'decision', decisionId: opt.decisionId }) : undefined}
                  chooseDisabled={busy}
                  chooseHint={verbLine(card.data?.verbCopy, 'decision')}
                />
              ))}
              {mode === 'full' &&
                (!customOpen ? (
                  <button
                    onClick={() => setCustomOpen(true)}
                    title={verbLine(card.data?.verbCopy, 'custom')}
                    className="rounded-2xl border border-dashed border-[#d3c6b2] p-4 text-left text-sm text-muted-foreground transition hover:border-foreground hover:text-foreground sm:col-span-2 dark:border-border"
                  >
                    Write your own resolution - settle it in your words
                  </button>
                ) : (
                  <div className="rounded-2xl border border-[#d8cde9] bg-[#f5f0fb] p-4 sm:col-span-2 dark:border-acc/30 dark:bg-acc-soft">
                    <input
                      autoFocus
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customText.trim()) resolve.mutate({ choice: 'custom', resolutionText: customText.trim() });
                      }}
                      placeholder="The resolution, in one sentence…"
                      className="w-full rounded-lg border border-[#d8cde9] bg-white px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-acc dark:border-border dark:bg-card"
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        disabled={busy || !customText.trim()}
                        onClick={() => customText.trim() && resolve.mutate({ choice: 'custom', resolutionText: customText.trim() })}
                        className={primaryVerbClass}
                      >
                        Settle it
                      </button>
                      <button onClick={() => { setCustomOpen(false); setCustomText(''); }} className={verbClass}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
            </div>
            {mode === 'full' && composed && card.data?.consequence && (
              <p className="mt-3 text-sm leading-5 text-muted-foreground">{card.data.consequence}</p>
            )}
          </div>

        </>
      )}

      {/* The verb rail sits OUTSIDE the card-load branch on purpose. It used to live in the success
          branch, so when the card query failed (retry:false, no retry control) the user lost "Not a
          conflict", Snooze and Ask along with it — and a demoted conflict appears on no other
          surface, so the item was stuck permanently. None of these verbs need the card: the resolve
          mutation keys on task.briefingItemId, not on the decisions. */}
      {!card.isLoading && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
            {mode === 'eval' ? (
              <>
                <button
                  disabled={busy}
                  onClick={() => resolve.mutate({ choice: 'not_conflict' })}
                  className={verbClass}
                  title={verbLine(card.data?.verbCopy, 'not_conflict')}
                >
                  These don't conflict
                </button>
                <button disabled={busy || cardUnavailable} onClick={() => setMode('full')} className={primaryVerbClass}>
                  It is a conflict →
                </button>
              </>
            ) : (
              <>
                <button
                  disabled={busy}
                  onClick={() => resolve.mutate({ choice: 'not_conflict' })}
                  className={verbClass}
                  title={verbLine(card.data?.verbCopy, 'not_conflict')}
                >
                  Not a conflict
                </button>
                {!snoozeOpen ? (
                  <button
                    disabled={busy}
                    onClick={() => setSnoozeOpen(true)}
                    className={verbClass}
                    title={verbLine(card.data?.verbCopy, 'snooze')}
                  >
                    Snooze
                  </button>
                ) : (
                  <span className="flex items-center gap-1.5 rounded-full border border-[#d7cab7] bg-[#fffaf2] py-1 pl-3 pr-1 text-sm dark:border-border dark:bg-card">
                    <span className="text-muted-foreground">Snooze:</span>
                    <button
                      disabled={busy}
                      onClick={() => resolve.mutate({ choice: 'snooze', snoozeMode: 'timer', snoozeUntil: tomorrowNineAmIso() })}
                      className="rounded-full px-2 py-0.5 transition hover:bg-foreground hover:text-card"
                      title={snoozeLine(card.data?.snoozeCopy, 'timer')}
                    >
                      tomorrow 9a
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => resolve.mutate({ choice: 'snooze', snoozeMode: 'until_inflow' })}
                      className="rounded-full px-2 py-0.5 transition hover:bg-foreground hover:text-card"
                      title={snoozeLine(card.data?.snoozeCopy, 'until_inflow')}
                    >
                      until it's in flow
                    </button>
                  </span>
                )}
                {/* Needs the two positions to fill the question — hidden when the card didn't load. */}
                {options.length > 0 && (
                  <button
                    onClick={() => onNav(`ask?q=${encodeURIComponent(askQuestionForConflict(title, options.map((o) => ({ author: o.author, text: o.decisionText }))))}`)}
                    className={verbClass}
                    title="Open Ask with this conflict filled in - nothing is sent until you press enter"
                  >
                    Ask about this
                  </button>
                )}
              </>
            )}
        </div>
      )}

      {/* Move it forward — the three-verb interruption ladder (spec §1) plus the zero-scope
          calendar/mail prefills. Ported from the brief's popup, which was the only surface that
          ever had them; the workroom shipped without, so the actions looked deleted (bug-557). */}
      {mode === 'full' && !card.isLoading && !cardUnavailable && (
            <div className="mt-5 border-t border-[#e6dccd] pt-4 dark:border-border">
              <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">Move it forward</p>
              {teammates.length > 0 && (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select
                      className="rounded-lg border border-[#e1d7c8] bg-input-background px-2.5 py-1.5 text-sm outline-none focus:border-acc dark:border-border"
                      value={teammateId}
                      onChange={(e) => setTeammateId(e.target.value)}
                      disabled={busy}
                      aria-label="Loop in a teammate"
                    >
                      <option value="">Loop in a teammate…</option>
                      {teammates.map((m) => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.display_name}
                        </option>
                      ))}
                    </select>
                    <input
                      className="min-w-0 flex-1 rounded-lg border border-[#e1d7c8] bg-input-background px-2.5 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-acc dark:border-border"
                      placeholder="Note (for Ping)"
                      value={pingNote}
                      onChange={(e) => setPingNote(e.target.value)}
                      disabled={busy}
                      aria-label="Ping note"
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap items-start gap-2">
                    <VerbButton
                      label="Route"
                      line={verbLine(card.data?.verbCopy, 'route')}
                      disabled={busy || !teammateId}
                      onClick={() => resolve.mutate({ choice: 'route', routeUserId: teammateId })}
                    />
                    <VerbButton
                      label="Blocked on them"
                      line={verbLine(card.data?.verbCopy, 'blocked')}
                      disabled={busy || !teammateId}
                      onClick={() => resolve.mutate({ choice: 'blocked', blockedOnUserId: teammateId })}
                    />
                    <VerbButton
                      label="Ping"
                      line={verbLine(card.data?.verbCopy, 'ping')}
                      disabled={busy || !teammateId || !pingNote.trim()}
                      onClick={() => resolve.mutate({ choice: 'ping', pingUserId: teammateId, note: pingNote.trim() })}
                    />
                  </div>
                </>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={verbClass}
                  onClick={() =>
                    window.open(
                      calendarEventUrl(
                        `Discuss: ${title}`,
                        // Canonical live route. This string is baked into a Google Calendar invite
                        // body — a permanent external record — so it must not point at a retired
                        // address that merely happens to redirect today.
                        `Resolving a decision conflict surfaced in Terum - ${window.location.origin}/#/briefing`,
                      ),
                      '_blank',
                      'noreferrer',
                    )
                  }
                >
                  Schedule sync
                </button>
                <button
                  type="button"
                  className={verbClass}
                  onClick={() =>
                    window.open(
                      followUpMailto(`Re: ${title}`, `Following up on the decision conflict "${title}" surfaced in Terum.`),
                      '_blank',
                      'noreferrer',
                    )
                  }
                >
                  Follow up
                </button>
              </div>
            </div>
      )}

      {resolve.isError && (
        <p className="mt-3 text-sm text-destructive">Couldn't save that. Please try again - nothing was changed.</p>
      )}
      {note && (
        <div role="status" className="mt-4 flex items-start gap-3 rounded-xl border border-[#d9cde9] bg-[#f3eef9] px-3 py-3 text-sm text-[#4a3d6b] dark:border-acc/30 dark:bg-acc-soft dark:text-foreground">
          <CheckCircle2 size={17} className="mt-0.5 shrink-0" />
          <p>{note}</p>
        </div>
      )}

      {task.conversationId && (
        <div className="mt-5 border-t border-[#e6dccd] pt-4 text-xs dark:border-border">
          <button
            onClick={() => onNav(`conversations/${task.conversationId}`)}
            className="inline-flex items-center gap-1 font-medium text-acc transition hover:underline"
          >
            Open the source conversation <ArrowUpRight size={12} />
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * One side of a conflict, and — in full mode — the path you'd pick by choosing it. The position and the
 * option ARE the same decision, so they're one card: who · the decision · why it's the stronger read ·
 * where it came from · choose it. The old split rendered `decisionText` twice (a muted quote above, the
 * same string bold below) with only the optional LLM `judgment` to tell them apart, so a composition
 * failure produced two identical blocks.
 *
 * `onChoose` absent = read-only (the eval-gate view, where the user hasn't yet affirmed a conflict
 * exists — offering "pick a side" there is exactly the R1 failure the band gate prevents).
 */
function PositionCard({
  option,
  onNav,
  accented = false,
  onChoose,
  chooseDisabled,
  chooseHint,
}: {
  option: ConflictCardResponse['options'][number] | undefined;
  onNav: (id: string) => void;
  accented?: boolean;
  onChoose?: () => void;
  chooseDisabled?: boolean;
  chooseHint?: string;
}) {
  if (!option) return null;
  const attribution = [option.author, option.source ? siteLabel(option.source) : null, shortDate(option.decidedAt)]
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      className={`flex flex-col rounded-2xl border p-4 ${accented ? 'border-[#d8cde9] bg-[#f5f0fb] dark:border-acc/30 dark:bg-acc-soft' : 'border-[#e4dacb] bg-[#fffaf3] dark:border-border dark:bg-muted'}`}
    >
      <p className="text-[15px] font-semibold leading-5">{option.author ?? 'Position'}</p>
      <p className="mt-1.5 text-sm leading-5 text-muted-foreground">“{option.decisionText}”</p>
      {option.judgment && <p className="mt-2 text-sm leading-5">{option.judgment}</p>}
      {option.sourceConversationId ? (
        <button
          onClick={() => onNav(`conversations/${option.sourceConversationId}`)}
          className="mt-3 inline-flex items-center gap-1 self-start text-xs font-medium text-acc transition hover:underline"
        >
          {attribution || 'Open source'} <ArrowUpRight size={12} />
        </button>
      ) : (
        attribution && <p className="mt-3 text-xs text-muted-foreground">{attribution}</p>
      )}
      {onChoose && (
        <button
          disabled={chooseDisabled}
          onClick={onChoose}
          title={chooseHint}
          className={`mt-3 inline-flex items-center gap-1 self-start rounded-full border px-3 py-1 text-xs font-medium transition disabled:opacity-50 ${accented ? 'border-acc/40 text-acc hover:bg-acc hover:text-card' : 'border-[#d7cab7] text-[#8b6546] hover:bg-foreground hover:text-card dark:border-border dark:text-muted-foreground'}`}
        >
          Choose this path <CheckCircle2 size={13} />
        </button>
      )}
    </div>
  );
}

/** A verb button with its canonical one-line self-description rendered beneath it, never paraphrased. */
function VerbButton({
  label,
  line,
  disabled,
  onClick,
}: {
  label: string;
  line: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <span className="flex max-w-[220px] flex-col gap-1">
      <button disabled={disabled} onClick={onClick} className={verbClass} title={line}>
        {label}
      </button>
      <span className="px-1 text-[11px] leading-4 text-muted-foreground">{line}</span>
    </span>
  );
}

/** Mechanical to-do room — a real brief task (linear / github_review / meeting_prep / …). */
function TaskRoom({
  task,
  onNav,
  done,
  onToggleDone,
}: {
  task: BriefTask;
  onNav: (id: string) => void;
  done: boolean;
  onToggleDone: () => void;
}) {
  const action = STREAM_ACTION[task.stream] ?? 'Open';
  const open = task.conversationId
    ? () => onNav(`conversations/${task.conversationId}`)
    : task.externalUrl
      ? () => window.open(task.externalUrl!, '_blank', 'noreferrer')
      : null;
  return (
    <section className="rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
      <div className="flex items-start gap-3">
        <span className={`grid size-10 shrink-0 place-items-center rounded-xl ${done ? 'bg-[#e4eadf] text-[#62725c]' : 'bg-[#dce8ec] text-[#527c90]'}`}>
          {done ? <Check size={19} /> : <Target size={19} />}
        </span>
        <div>
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">
            Task · {STREAM_LABELS[task.stream] ?? task.stream}
            {task.dueLabel ? ` · ${task.dueLabel}` : ''}
          </p>
          <h2 className={`mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em] ${done ? 'text-muted-foreground line-through' : ''}`}>
            {task.title}
          </h2>
        </div>
      </div>
      {task.detail && <p className="mt-3 max-w-[560px] text-[15px] leading-6 text-muted-foreground">{task.detail}</p>}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {open && (
          <button onClick={open} className={primaryVerbClass}>
            {action} {task.externalUrl && !task.conversationId ? '↗' : '→'}
          </button>
        )}
        <button onClick={onToggleDone} className={verbClass}>
          {done ? 'Not done' : 'Mark done'}
        </button>
      </div>
    </section>
  );
}

/** Snoozed-this-session conflict — no un-snooze rail exists, so this states the wake honestly. */
function PausedRoom({ task, wake }: { task: BriefTask; wake?: string }) {
  return (
    <section className="rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[#eee5d5] text-[#887157]">
          <Clock3 size={19} />
        </span>
        <div>
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">Paused</p>
          <h2 className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em]">{task.title}</h2>
        </div>
      </div>
      {task.detail && <p className="mt-3 max-w-[560px] text-[15px] leading-6 text-muted-foreground">{task.detail}</p>}
      <div className="mt-4">
        <span className="rounded-full bg-[#eee5d5] px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.06em] text-[#887157]">
          {wake ?? 'snoozed'}
        </span>
      </div>
    </section>
  );
}

/** A durable record row's workspace (GET /api/surfaces/team/resolutions).
 *
 * The record's one verb is DISPUTE, and it is now wired: POST /resolutions/:id/dispute keys on the
 * conversation PAIR the ledger row durably holds, so it works long after the briefing item that
 * surfaced the conflict is gone. (This card was read-only while that route was still the named
 * follow-up — it exists now.) It is also what makes the one-click settle on the conflict cards
 * above safe: an outcome you can object to is not a one-way door.
 *
 * Only a `resolved` row can be disputed; `dismissed` is terminal and `disputed` is not
 * re-disputable, so the verb is hidden in those states rather than offered and rejected. */
function RecordRoom({ record, onNav }: { record: ResolutionRecord; onNav: (id: string) => void }) {
  const dismissed = record.status === 'dismissed';
  const disputed = record.status === 'disputed';
  const sameConv = record.conversationA === record.conversationB;
  const dispute = useDisputeResolution();
  const [objecting, setObjecting] = useState(false);
  const [objection, setObjection] = useState('');
  return (
    <section className="rounded-[24px] border border-[#2a2447] bg-[#322b4d] p-5 text-[#fbf5e9] shadow-[0_16px_34px_rgba(74,58,107,.14)] sm:p-6">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/10 text-[#ded7ec]">
          {disputed ? <Scale size={19} /> : <Check size={19} />}
        </span>
        <div className="min-w-0">
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-[#c7c2d4]">
            {dismissed ? 'Record · dismissed' : disputed ? 'Record · disputed' : 'Decision record'}
          </p>
          {/* Short title. `settledText` is a full sentence written by whoever settled it (or a
              verbatim decision snapshot), so as the headline it ran to three and four lines and
              buried the metadata under it. The record's headline is the SUBJECT; the sentence
              itself is the "What" line below, in full and unclipped. DM Sans, not the display
              serif — that is reserved for mastheads and section heads (#210). */}
          <h2 className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em]">
            {recordTitle(record)}
          </h2>
        </div>
      </div>
      <div className="mt-4 grid gap-1 text-sm">
        {/* The settled sentence in full — it is no longer the headline, so nothing is lost by the
            shorter title above. */}
        {record.settledText && (
          <p>
            <span className="text-[#c2bcd2]">What</span> {record.settledText}
          </p>
        )}
        {record.rationale && (
          <p>
            <span className="text-[#c2bcd2]">Why</span> {record.rationale}
          </p>
        )}
        <p>
          <span className="text-[#c2bcd2]">Settled by</span> {record.resolvedByName ?? 'a teammate'}
        </p>
        <p>
          <span className="text-[#c2bcd2]">When</span> {shortDate(record.resolvedAt) ?? '-'}
        </p>
        <p>
          <span className="text-[#c2bcd2]">State</span>{' '}
          {dismissed
            ? 'dismissed · not a conflict'
            : disputed
              ? `disputed${record.disputedByName ? ` by ${record.disputedByName}` : ''}${shortDate(record.disputedAt ?? null) ? ` · ${shortDate(record.disputedAt ?? null)}` : ''}`
              : 'recorded'}
        </p>
        {disputed && record.disputeNote && (
          <p>
            <span className="text-[#c2bcd2]">Objection</span> “{record.disputeNote}”
          </p>
        )}
      </div>
      {(record.decisionAText || record.decisionBText) && (
        <div className="mt-4 border-t border-white/10 pt-3">
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-[#c7c2d4]">The two positions</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {record.decisionAText && (
              <div className="rounded-xl border border-white/10 bg-white/[.05] p-3 text-sm leading-5">
                {record.authorAName && <p className="mb-1 font-semibold">{record.authorAName}</p>}
                <p className="text-[#dcd7e6]">{record.decisionAText}</p>
              </div>
            )}
            {record.decisionBText && (
              <div className="rounded-xl border border-white/10 bg-white/[.05] p-3 text-sm leading-5">
                {record.authorBName && <p className="mb-1 font-semibold">{record.authorBName}</p>}
                <p className="text-[#dcd7e6]">{record.decisionBText}</p>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => onNav('conversations/' + record.conversationA)}
          className="rounded-full border border-white/15 bg-white/[.06] px-3.5 py-1.5 text-sm text-[#e7e2f1] transition hover:bg-white/[.12]"
        >
          {sameConv ? 'Open the source' : 'Open source A'}
        </button>
        {!sameConv && (
          <button
            onClick={() => onNav('conversations/' + record.conversationB)}
            className="rounded-full border border-white/15 bg-white/[.06] px-3.5 py-1.5 text-sm text-[#e7e2f1] transition hover:bg-white/[.12]"
          >
            Open source B
          </button>
        )}
        {/* The record's one verb. Offered only where the backend will accept it: `dismissed` is
            terminal and `disputed` is not re-disputable. */}
        {!dismissed && !disputed && !objecting && (
          <button
            onClick={() => setObjecting(true)}
            className="rounded-full border border-white/15 px-3.5 py-1.5 text-sm text-[#e0c3c3] transition hover:bg-white/[.12]"
          >
            Dispute this
          </button>
        )}
      </div>

      {objecting && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <label
            htmlFor={`objection-${record.id}`}
            className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-[#c7c2d4]"
          >
            Why this isn&rsquo;t settled
          </label>
          <textarea
            id={`objection-${record.id}`}
            rows={2}
            maxLength={500}
            value={objection}
            onChange={(e) => setObjection(e.target.value)}
            disabled={dispute.isPending}
            placeholder="Optional - what the record gets wrong"
            className="mt-2 w-full rounded-xl border border-white/15 bg-white/[.06] p-3 text-sm leading-5 text-[#fbf5e9] outline-none transition placeholder:text-[#a49dbb] focus:border-white/35"
          />
          <p className="mt-2 text-xs leading-5 text-[#c2bcd2]">
            This reopens the conflict for the whole team - it goes back on the board rather than
            staying settled.
          </p>
          {dispute.isError && (
            <p className="mt-2 text-sm text-[#f0a8a8]">
              Couldn&rsquo;t record that objection. Nothing was changed - try again.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              disabled={dispute.isPending}
              onClick={() =>
                dispute.mutate(
                  { id: record.id, disputeNote: objection },
                  { onSuccess: () => { setObjecting(false); setObjection(''); } },
                )
              }
              className="rounded-full bg-[#e0c3c3] px-3.5 py-1.5 text-sm font-medium text-[#3b2f2f] transition hover:bg-[#eed6d6] disabled:opacity-60"
            >
              {dispute.isPending ? 'Reopening…' : 'Reopen it →'}
            </button>
            <button
              disabled={dispute.isPending}
              onClick={() => { setObjecting(false); setObjection(''); dispute.reset(); }}
              className="rounded-full border border-white/15 px-3.5 py-1.5 text-sm text-[#e7e2f1] transition hover:bg-white/[.12]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function SessionRecordRoom({ outcome }: { outcome: ConflictOutcome }) {
  const dismissed = outcome.verb === 'not_conflict';
  return (
    <section className="rounded-[24px] border border-[#2a2447] bg-[#322b4d] p-5 text-[#fbf5e9] shadow-[0_16px_34px_rgba(74,58,107,.14)] sm:p-6">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/10 text-[#ded7ec]">
          <Check size={19} />
        </span>
        <div>
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-[#c7c2d4]">
            {dismissed ? 'Record · dismissed' : 'Decision record'}
          </p>
          <h2 className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em]">{outcome.title ?? 'Settled conflict'}</h2>
        </div>
      </div>
      <div className="mt-4 grid gap-1 text-sm">
        {outcome.how && (
          <p>
            <span className="text-[#c2bcd2]">How</span> {outcome.how}
          </p>
        )}
        <p>
          <span className="text-[#c2bcd2]">Settled by</span> you
        </p>
        <p>
          <span className="text-[#c2bcd2]">When</span> just now
        </p>
        <p>
          <span className="text-[#c2bcd2]">State</span> {dismissed ? 'dismissed · not a conflict' : 'recorded · propagating'}
        </p>
      </div>
    </section>
  );
}
